import type { FieldDef, FieldType, NodeType } from '@dispositio/core';
import { useState } from 'react';
import { de } from '../../i18n/de';
import { newFieldDef, suggestionsFor } from '../../lib/fieldSuggestions';
import { useStoryEnv } from '../../state/storyEnv';
import { toast } from '../../state/toast';

type Kind = FieldType | 'choice';
const KINDS: Kind[] = ['text', 'longtext', 'number', 'date', 'image', 'choice'];

/**
 * „+ Feld“: sinnvolle Vorschläge mit einem Klick oder ein selbst entworfenes Feld (Name, Art, ggf. Auswahl). Das Feld kommt sofort
 * in die Klasse – jeder Eintrag dieses Bereichs hat es, auch alle künftigen.
 */
export function FieldAdder({ type }: { type: NodeType }) {
  const { actions } = useStoryEnv();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<Kind>('text');
  const [options, setOptions] = useState('');
  const [busy, setBusy] = useState(false);
  const suggestions = suggestionsFor(type);

  const add = async (def: FieldDef): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      if (!(await actions.updateType(type.id, { fields: [...type.fields, def] }))) return;
      toast.ok(de.design.fieldAdded(def.label));
      setLabel('');
      setOptions('');
      setKind('text');
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="btn ghost small field-add" onClick={() => setOpen(true)} title={de.design.addFieldHint} data-testid="field-add">
        + {de.design.addField}
      </button>
    );
  }
  const opts = options
    .split(',')
    .map((o) => o.trim())
    .filter(Boolean);
  const valid = label.trim() !== '' && (kind !== 'choice' || opts.length >= 2);
  return (
    <div className="field-adder" data-testid="field-adder">
      <div className="row between">
        <strong className="small">{de.design.addFieldTitle}</strong>
        <button type="button" className="btn ghost small" onClick={() => setOpen(false)} data-testid="field-adder-close">
          {de.design.fieldDone}
        </button>
      </div>
      {suggestions.length > 0 && (
        <div className="col">
          <div className="small muted">{de.design.suggestions}</div>
          <div className="chip-row">
            {suggestions.map((s) => (
              <button key={s.label} type="button" className="chip-btn" disabled={busy} onClick={() => void add(newFieldDef(type, s))} data-testid="field-suggestion">
                + {s.label}
              </button>
            ))}
          </div>
        </div>
      )}
      <form
        className="col"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) void add(newFieldDef(type, { label, type: kind === 'choice' ? 'text' : kind, ...(kind === 'choice' ? { options: opts } : {}) }));
        }}
      >
        <div className="small muted">{de.design.ownField}</div>
        <div className="field-adder-row">
          <input autoFocus={suggestions.length === 0} value={label} onChange={(e) => setLabel(e.target.value)} placeholder={de.design.fieldName} aria-label={de.design.fieldName} maxLength={120} data-testid="field-new-label" />
          <select value={kind} onChange={(e) => setKind(e.target.value as Kind)} aria-label={de.design.fieldType} data-testid="field-new-type">
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {de.design.fieldTypes[k]}
              </option>
            ))}
          </select>
          <button type="submit" className="btn small primary" disabled={!valid || busy} data-testid="field-new-add">
            {de.design.fieldAdd}
          </button>
        </div>
        {kind === 'choice' && <input value={options} onChange={(e) => setOptions(e.target.value)} placeholder={de.design.fieldOptions} aria-label={de.design.fieldOptions} data-testid="field-new-options" />}
      </form>
      <div className="small muted">{de.design.addFieldHint}</div>
    </div>
  );
}
